/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Hook execution status
 */
export const HookStatus = {
  Success: "success",
  Failure: "failure",
  Pending: "pending",
  Blocked: "blocked",
} as const;
/**
 * Hook execution status
 */
export type HookStatus = OpenEnum<typeof HookStatus>;

/**
 * Summary information for a hook trace
 */
export type HookTraceSummary = {
  /**
   * Reason set when hook_status is 'blocked' (e.g. shadow-MCP guard rejection)
   */
  blockReason?: string | undefined;
  /**
   * Event source (from materialized column)
   */
  eventSource?: string | undefined;
  /**
   * Speakeasy URN associated with this hook trace
   */
  gramUrn: string;
  /**
   * Hook source (from attributes.gram.hook.source)
   */
  hookSource?: string | undefined;
  /**
   * Hook execution status
   */
  hookStatus?: HookStatus | undefined;
  /**
   * Total number of logs in this trace
   */
  logCount: number;
  /**
   * Skill name (from materialized column, only for Skill tool)
   */
  skillName?: string | undefined;
  /**
   * Earliest log timestamp in Unix nanoseconds (string for JS int64 precision)
   */
  startTimeUnixNano: string;
  /**
   * Tool name (from materialized column)
   */
  toolName?: string | undefined;
  /**
   * Tool call source (from materialized column)
   */
  toolSource?: string | undefined;
  /**
   * Trace ID (32 hex characters)
   */
  traceId: string;
  /**
   * User email (from attributes.user.email)
   */
  userEmail?: string | undefined;
};

/** @internal */
export const HookStatus$inboundSchema: z.ZodMiniType<HookStatus, unknown> =
  openEnums.inboundSchema(HookStatus);

/** @internal */
export const HookTraceSummary$inboundSchema: z.ZodMiniType<
  HookTraceSummary,
  unknown
> = z.pipe(
  z.object({
    block_reason: z.optional(z.string()),
    event_source: z.optional(z.string()),
    gram_urn: z.string(),
    hook_source: z.optional(z.string()),
    hook_status: z.optional(HookStatus$inboundSchema),
    log_count: z.int(),
    skill_name: z.optional(z.string()),
    start_time_unix_nano: z.string(),
    tool_name: z.optional(z.string()),
    tool_source: z.optional(z.string()),
    trace_id: z.string(),
    user_email: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "block_reason": "blockReason",
      "event_source": "eventSource",
      "gram_urn": "gramUrn",
      "hook_source": "hookSource",
      "hook_status": "hookStatus",
      "log_count": "logCount",
      "skill_name": "skillName",
      "start_time_unix_nano": "startTimeUnixNano",
      "tool_name": "toolName",
      "tool_source": "toolSource",
      "trace_id": "traceId",
      "user_email": "userEmail",
    });
  }),
);

export function hookTraceSummaryFromJSON(
  jsonString: string,
): SafeParseResult<HookTraceSummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => HookTraceSummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'HookTraceSummary' from JSON`,
  );
}
